/**
 * <load-more> Custom Element
 * Append-based "Load More" pagination for collection pages.
 *
 * Features:
 * - Appends products to grid without replacing existing ones
 * - Retains scroll position after loading
 * - Persists state (loaded pages + scroll) via sessionStorage for back-navigation
 * - SEO: canonical stays on page 1, uses history.pushState for URL updates
 * - Shows "X of Y products" count
 * - "View More" CTA hides when all products loaded
 */

/**
 * Safari can restore a collection PLP from bfcache with product-card gallery images
 * that still have valid src/srcset but a lost decoded bitmap (naturalWidth === 0).
 * Re-assigning src/srcset forces a reload. Most visible after load-more + PDP navigation.
 */
function restoreProductCardImagesAfterBfcache() {
  const grid = document.querySelector('.product-grid');
  if (!grid) return;

  for (const item of grid.querySelectorAll('.product-grid__item[style*="content-visibility"]')) {
    item.style.removeProperty('content-visibility');
    item.style.removeProperty('view-transition-name');
  }

  for (const img of grid.querySelectorAll('slideshow-slide[aria-hidden="false"] .product-media__image')) {
    if (!(img instanceof HTMLImageElement)) continue;
    if (img.naturalWidth > 0) continue;

    const src = img.getAttribute('src');
    if (!src) continue;

    const srcset = img.getAttribute('srcset');
    img.removeAttribute('src');
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
    if (srcset) img.setAttribute('srcset', srcset);
  }
}

let bfcacheImageRestoreRegistered = false;

if (!bfcacheImageRestoreRegistered) {
  bfcacheImageRestoreRegistered = true;

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    requestAnimationFrame(restoreProductCardImagesAfterBfcache);
  });
}

if (!customElements.get('load-more')) {
  class LoadMore extends HTMLElement {
    #sectionId;
    #gridSelector = '[ref="grid"]';
    #itemSelector = '[ref="cards[]"]';
    #storageKey;
    #isLoading = false;
    #loadedPages = new Set();

    constructor() {
      super();

      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
    }

    connectedCallback() {
      this.#sectionId = this.dataset.sectionId;
      this.#storageKey = this.#buildStorageKey();

      this.#loadedPages.add(this.#getCurrentPage());

      this.addEventListener('click', this.#handleClick);
      this.#restoreState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick);
    }

    get #grid() {
      return this.querySelector(this.#gridSelector);
    }

    get #nextButton() {
      return this.querySelector('[data-load-more-next]');
    }

    get #nextWrapper() {
      return this.querySelector('[data-pagination-next]');
    }

    get #prevWrapper() {
      return this.querySelector('[data-pagination-prev]');
    }

    #buildStorageKey() {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      params.delete('page');
      params.delete('section_id');
      const filterString = params.toString();
      return `load-more-${url.pathname}${filterString ? `?${filterString}` : ''}`;
    }

    #getCurrentPage() {
      const url = new URL(window.location.href);
      return parseInt(url.searchParams.get('page') || '1', 10);
    }

    #handleClick = (e) => {
      const loadButton = e.target.closest('[data-load-button]');
      if (loadButton) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const direction = loadButton.dataset.loadDirection || 'next';
        this.#loadPage(loadButton, direction);
        return;
      }

      const productLink = e.target.closest('a[href]');
      if (productLink && !productLink.dataset.loadButton) {
        this.#saveState(productLink.getAttribute('href'));
      }
    };

    async #loadPage(button, direction = 'next') {
      if (this.#isLoading) return;

      const href = button.getAttribute('href');
      if (!href) return;

      this.#isLoading = true;
      this.#setButtonLoadingState(button, true);

      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('section_id', this.#sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const fetchedGrid = doc.querySelector(this.#gridSelector);
        if (!fetchedGrid) throw new Error('Grid not found in response');

        const newItems = fetchedGrid.querySelectorAll(`:scope > ${this.#itemSelector}`);
        if (newItems.length === 0) throw new Error('No items found');

        const grid = this.#grid;
        if (!grid) throw new Error('Local grid not found');

        const fragment = document.createDocumentFragment();
        for (const item of newItems) {
          fragment.appendChild(item);
        }

        if (direction === 'prev') {
          grid.prepend(fragment);
        } else {
          grid.appendChild(fragment);
        }

        if (direction === 'prev') {
          const fetchedPrev = doc.querySelector('[data-pagination-prev]');
          const currentPrev = this.#prevWrapper;
          if (currentPrev && fetchedPrev) {
            currentPrev.innerHTML = fetchedPrev.innerHTML;
          } else if (currentPrev && !fetchedPrev) {
            currentPrev.innerHTML = '';
          }
        } else {
          const fetchedNext = doc.querySelector('[data-pagination-next]');
          const currentNext = this.#nextWrapper;
          if (currentNext && fetchedNext) {
            currentNext.innerHTML = fetchedNext.innerHTML;
          } else if (currentNext && !fetchedNext) {
            currentNext.innerHTML = '';
          }
        }

        const pageNum = parseInt(url.searchParams.get('page') || '1', 10);
        this.#loadedPages.add(pageNum);

        this.#updateURL(pageNum);
        this.#updateCount();
        this.#updateSessionStorage();
      } catch (error) {
        console.error('[load-more]', error);
        this.#showError();
      } finally {
        this.#isLoading = false;
        this.#setButtonLoadingState(button, false);
      }
    }

    #setButtonLoadingState(button, isLoading) {
      if (!button) return;

      const textEl = button.querySelector('[data-load-text]');
      const loaderEl = button.querySelector('[data-loader]');

      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('is-loading');
        if (textEl) textEl.hidden = true;
        if (loaderEl) loaderEl.hidden = false;
      } else {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        if (textEl) textEl.hidden = false;
        if (loaderEl) loaderEl.hidden = true;
      }
    }

    #updateURL(page) {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page.toString());
      } else {
        url.searchParams.delete('page');
      }
      history.pushState({ page }, '', url.toString());
    }

    #updateCount() {
      const countEl = this.querySelector('[data-load-more-count]');
      if (!countEl) return;

      const totalCount = parseInt(countEl.dataset.totalItems || '0', 10);
      const pageSize = parseInt(countEl.dataset.pageSize || '24', 10);

      const maxPage = Math.max(...this.#loadedPages);
      const loadedCount = Math.min(maxPage * pageSize, totalCount);

      countEl.textContent = `${loadedCount} of ${totalCount} products`;

      const progressBar = this.querySelector('[data-load-more-progress-bar]');
      const progressEl = this.querySelector('[data-load-more-progress]');
      if (progressBar && totalCount > 0) {
        const percent = Math.min((loadedCount / totalCount) * 100, 100);
        progressBar.style.width = `${percent}%`;
      }
      if (progressEl) {
        progressEl.setAttribute('aria-valuenow', loadedCount);
      }

      if (loadedCount >= totalCount) {
        const button = this.#nextButton;
        if (button) button.hidden = true;
      }
    }

    #showError() {
      const nextWrapper = this.#nextWrapper;
      if (!nextWrapper) return;

      const errorEl = document.createElement('p');
      errorEl.className = 'load-more__error';
      errorEl.textContent = 'Error loading products. Please try again.';
      errorEl.setAttribute('role', 'alert');
      nextWrapper.appendChild(errorEl);

      setTimeout(() => errorEl.remove(), 5000);
    }

    #saveState(clickedHref) {
      const state = {
        pages: [...this.#loadedPages],
        clickedHref: clickedHref,
        scrollY: window.scrollY,
      };
      sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
    }

    #updateSessionStorage() {
      const existing = sessionStorage.getItem(this.#storageKey);
      const state = existing ? JSON.parse(existing) : {};
      state.pages = [...this.#loadedPages];
      sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
    }

    async #restoreState() {
      const raw = sessionStorage.getItem(this.#storageKey);
      if (!raw) return;

      let state;
      try {
        state = JSON.parse(raw);
      } catch {
        sessionStorage.removeItem(this.#storageKey);
        return;
      }

      const { pages, clickedHref, scrollY } = state;
      sessionStorage.removeItem(this.#storageKey);

      if (!clickedHref) return;

      if (!pages || pages.length <= 1) {
        this.#scrollToProduct(clickedHref, scrollY);
        return;
      }

      const sortedPages = pages.sort((a, b) => a - b);
      const currentPage = this.#getCurrentPage();

      if (currentPage !== 1 && currentPage !== sortedPages[0]) {
        return;
      }

      this.#setButtonLoadingState(this.#nextButton, true);

      for (const pageNum of sortedPages) {
        if (this.#loadedPages.has(pageNum)) continue;

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('page', pageNum.toString());
          url.searchParams.set('section_id', this.#sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) continue;

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedGrid = doc.querySelector(this.#gridSelector);
          if (!fetchedGrid) continue;

          const newItems = fetchedGrid.querySelectorAll(`:scope > ${this.#itemSelector}`);
          const grid = this.#grid;
          if (!grid || newItems.length === 0) continue;

          const fragment = document.createDocumentFragment();
          for (const item of newItems) {
            fragment.appendChild(item);
          }
          grid.appendChild(fragment);
          this.#loadedPages.add(pageNum);
        } catch {
          break;
        }
      }

      const lastPage = Math.max(...sortedPages);
      const url = new URL(window.location.href);
      url.searchParams.set('page', lastPage.toString());
      url.searchParams.set('section_id', this.#sectionId);

      try {
        const response = await fetch(url.toString());
        if (response.ok) {
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fetchedNext = doc.querySelector('[data-pagination-next]');
          const currentNext = this.#nextWrapper;
          if (currentNext && fetchedNext) {
            currentNext.innerHTML = fetchedNext.innerHTML;
          } else if (currentNext && !fetchedNext) {
            currentNext.innerHTML = '';
          }
        }
      } catch {
        // Non-critical: button state may be stale
      }

      this.#setButtonLoadingState(this.#nextButton, false);
      this.#updateCount();
      this.#scrollToProduct(clickedHref, scrollY);
    }

    #scrollToProduct(href, fallbackScrollY) {
      if (!fallbackScrollY) return;

      requestAnimationFrame(() => {
        window.scrollTo({ top: fallbackScrollY, behavior: 'smooth' });
      });
    }
  }

  customElements.define('load-more', LoadMore);
}
